import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { DEVICE_CHECKS, SYSTEM_CHECKS, SYSTEM_STEP_MS, initialChecks, isSettled } from "./constants";
import type { Checks, UpdateCheck } from "./constants";
import { SYSTEM_DETECTORS } from "./systemChecks";
import CheckJourney from "./components/CheckJourney";
import DeviceTest from "./components/DeviceTest";

const CompatibilityTest: React.FC = () => {
  const { t } = useTranslation();
  const [systemChecks, setSystemChecks] = useState<Checks>(() => initialChecks(SYSTEM_CHECKS));
  const [deviceChecks, setDeviceChecks] = useState<Checks>(() => initialChecks(DEVICE_CHECKS));
  // 0 = not started; incremented on every run to remount the device test
  const [run, setRun] = useState(0);
  // True once the candidate has gone through every device step of the current run
  const [deviceDone, setDeviceDone] = useState(false);
  const handleDeviceDone = useCallback(() => setDeviceDone(true), []);

  // Run the system checks one after another
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const { key } of SYSTEM_CHECKS) {
        setSystemChecks((prev) => ({ ...prev, [key]: { status: "running", detail: "Checking…" } }));
        const [check] = await Promise.all([
          SYSTEM_DETECTORS[key](),
          new Promise((resolve) => setTimeout(resolve, SYSTEM_STEP_MS)),
        ]);
        if (cancelled) return;
        setSystemChecks((prev) => ({ ...prev, [key]: check }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateDeviceCheck = useCallback<UpdateCheck>((key, check) => {
    setDeviceChecks((prev) =>
      prev[key].status === check.status && prev[key].detail === check.detail
        ? prev
        : { ...prev, [key]: check },
    );
  }, []);

  const startTest = () => {
    setDeviceChecks(initialChecks(DEVICE_CHECKS));
    setDeviceDone(false);
    setRun((prev) => prev + 1);
  };

  const results = [...Object.values(systemChecks), ...Object.values(deviceChecks)];
  const systemDone = Object.values(systemChecks).every(isSettled);
  const deviceRunning = run > 0 && !deviceDone;
  const finished = run > 0 && deviceDone && results.every(isSettled);
  const failedCount = results.filter((c) => c.status === "fail").length;

  const allPassed = finished && failedCount === 0;
  useEffect(() => {
    if (allPassed) toast.success("All checks passed. Your device is compatible.");
  }, [allPassed]);

  return (
    <div className="md:-mt-8">
      <div className="text-white bg-primary my-4 py-4 px-4 mx-1 rounded-lg">
        <h5 className="text-xl mb-1">Check your device compatibility</h5>
        <p className="text-sm text-white/90">
          Make sure your browser, camera, microphone and face scan work before you take a
          proctored exam. This is only a test — nothing is saved or sent.
        </p>
      </div>

      <div className="space-y-4 mx-1 pb-4">
        <CheckJourney title="System checks" items={SYSTEM_CHECKS} checks={systemChecks} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-lg font-bold text-gray-800 mb-3">Camera & Microphone</h3>
            {run > 0 ? (
              <DeviceTest
                key={run}
                update={updateDeviceCheck}
                failedCount={failedCount}
                onDone={handleDeviceDone}
              />
            ) : (
              <div className="rounded-xl bg-gray-50 border border-dashed border-gray-300 w-[250px] h-[250px] max-w-full mx-auto flex flex-col items-center justify-center text-center p-4">
                <div className="text-4xl mb-3">📷</div>
                <p className="text-gray-600 text-sm max-w-sm">
                  Your browser will ask for permission to use your camera and microphone. Click
                  "Allow" when prompted.
                </p>
              </div>
            )}
            {finished ? (
              <div className="mt-4 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <button
                  onClick={startTest}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
                >
                  Retest
                </button>
                <Link
                  to={"/candidate/assessments/view-assessments"}
                  className="flex-1 py-3 px-4 bg-primary text-white text-center rounded-xl font-semibold hover:bg-primary/90 transition-colors"
                >
                  {t("upcoming-exams")}
                </Link>
              </div>
            ) : (
              <button
                onClick={startTest}
                disabled={!systemDone || deviceRunning}
                className="mt-4 w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!systemDone
                  ? "Running system checks…"
                  : deviceRunning
                    ? "Testing your device…"
                    : "Start device test"}
              </button>
            )}
          </div>

          <CheckJourney title="Device checks" items={DEVICE_CHECKS} checks={deviceChecks} vertical />
        </div>
      </div>
    </div>
  );
};

export default CompatibilityTest;
